import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@hulahoop/ui";
import { LinkSimple } from "@phosphor-icons/react";
import { useNavigationService } from "@/presentation/hooks/useNavigationService";
import { useToastStore } from "@/stores/useToastStore";

export function useCopyPageLink() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigation = useNavigationService();
  const showToast = useToastStore((state) => state.showToast);
  const [result, setResult] = useState<"copied" | "failed" | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${navigation.getPageOrigin()}${location.pathname}${location.search}${location.hash}`,
      );
      setResult("copied");
      showToast(t("shell.copied"), "success");
    } catch {
      setResult("failed");
      showToast(t("shell.copy_failed"), "error");
    }
  };
  return { copy, result };
}

export function CopyPageLink() {
  const { t } = useTranslation();
  const { copy, result } = useCopyPageLink();
  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("shell.copy_link")}
              onClick={() => void copy()}
            >
              <LinkSimple />
            </Button>
          }
        />
        <TooltipContent>{t("shell.copy_link")}</TooltipContent>
      </Tooltip>
      <span role="status" className="sr-only">
        {result === "copied" && t("shell.copied")}
        {result === "failed" && t("shell.copy_failed")}
      </span>
    </>
  );
}
